import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { useTranslation } from 'react-i18next';

import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import ArrowBackIos from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosOutlinedIcon from '@material-ui/icons/ArrowForwardIosOutlined';

import AccountStepper from 'components/AccountStepper/AccountStepper';
import Background from 'components/Layout/Background';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import DialogHeader from 'components/DialogHeader/DialogHeader';

import useStyles from 'pages/Accounts/Detail/Signature/styles/Documents';
import { RootStore } from 'stores/rootStore';
import { SignatureFlowStepId, SignatureStore } from 'stores/account/signatureStore';
import { Client, Currency, SignatureStatus } from 'enums'; //SignatureFlow
import Documents from './Documents';
import Requests from './Requests';
import Settings from './Settings';
import Portfolios from './Portfolios';

export interface Params {
  accountId: string;
}

interface Props {
  rootStore: RootStore;
  accountId: string;
}

export const InvalidState = (msg: string | React.ReactElement, inside?: boolean, details?: React.ReactElement) => (
  <Container maxWidth="md">
    <Background inside={inside}>
      <Typography variant="h2" align="center">
        {msg}
      </Typography>
      {details}
    </Background>
  </Container>
);

const Signature: React.FC<Props> = ({ rootStore, accountId }) => {
  const { t } = useTranslation();
  const { userStore } = rootStore.stores;
  const { activeAccount } = userStore;
  const signOnly = userStore.loggedUser?.client === Client.CLIENT_PORTAL_SIGN;
  const hedgingAllowed = userStore.activeAccount?.currency === Currency.CZK;
  const [store] = useState(new SignatureStore(rootStore, accountId, activeAccount?.pendingSignatureChanges, signOnly));
  const [abortDialogOpen, setAbortDialogOpen] = useState(false);
  const [completeDialogClosed, setCompleteDialogClosed] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const { flow, flowStep } = store;
  const classes = useStyles();

  const nextAccount = userStore.clientData?.accounts.find(
    (a) => a.accountId !== activeAccount?.accountId && (a.pendingSignature || a.pendingRequestSignature),
  );

  useEffect(() => {
    (async () => {
      await store.fetchAll(
        [SignatureStatus.SIGNED, SignatureStatus.EXPIRED, undefined].includes(
          rootStore.stores.userStore.activeAccount?.process,
        ),
      );
    })();
  }, [store, rootStore.stores.userStore.activeAccount?.process, activeAccount?.pendingSignatureChanges]);

  const documents = store.documents && store.documents.length > 0;
  const requests = store.requests && store.requests?.length > 0;

  if (!activeAccount?.pendingSignature && !activeAccount?.pendingRequestSignature && !requests) {
    return InvalidState(t('signature_nothingToSign'));
  }
  if (store.canceled) {
    return InvalidState(t('documents_sign_cancelled'), true);
  }
  if ((store.signed || store.canceled) && store.requestsResolved) {
    return InvalidState(
      t('documents_sign_complete_info'),
      true,
      nextAccount ? (
        <>
          <Typography paragraph align="center">
            <span
              dangerouslySetInnerHTML={{
                __html: t('documents_sign_complete_nextAccount', {
                  account: nextAccount.title,
                  interpolation: { escapeValue: true },
                }),
              }}
            />
          </Typography>
          <Typography paragraph align="center">
            <Button
              variant="contained"
              color="primary"
              onClick={() => userStore.setActiveAccount(nextAccount)}
              endIcon={<ArrowForwardIosOutlinedIcon />}
            >
              {t('documents_sign_complete_nextAccount_button')}
            </Button>
          </Typography>
        </>
      ) : undefined,
    );
  }

  const buttons = () => {
    return (
      <>
        <Button
          variant="contained"
          color="default"
          className={`${classes.actionButton}`}
          onClick={() => {
            setCompleteDialogClosed(true);
            setAbortDialogOpen(true);
          }}
          disabled={store.signed}
        >
          {t('documents_sign_abort')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={classes.actionButton}
          onClick={() => {
            setCompleteDialogClosed(true);
            setSmsCode('');
            store.requestCode();
          }}
          disabled={!store.canBeSigned}
        >
          {t('general_sign')}
        </Button>
      </>
    );
  };

  return flow && flowStep ? (
    <>
      <Container maxWidth="md">
        {flow.length > 1 && rootStore.stores.userStore.activeAccount?.process !== SignatureStatus.SIGNED && (
          <Container maxWidth="md" className={classes.wrapper}>
            <AccountStepper
              steps={flow.map((step) => ({ id: step.id, title: step.titleShort || step.title }))}
              activeId={flowStep}
              mobileBreak={480}
            />
          </Container>
        )}
        {[
          SignatureFlowStepId.SETTING_LONG_TERM,
          SignatureFlowStepId.SETTING_MID_TERM,
          SignatureFlowStepId.SETTING_SHORT_TERM,
        ].includes(flowStep) && <Settings store={store} hedgingAllowed={hedgingAllowed} />}

        {[
          SignatureFlowStepId.PORTFOLIO_LONG_TERM,
          SignatureFlowStepId.PORTFOLIO_MID_TERM,
          SignatureFlowStepId.PORTFOLIO_SHORT_TERM,
        ].includes(flowStep) && (
          <>
            <Portfolios store={store} />
            {store.canBeSigned && (
              <Container maxWidth="md">
                <div className={classes.buttonWrapper}>{buttons()}</div>
              </Container>
            )}
          </>
        )}

        {flowStep === SignatureFlowStepId.DOCS && (
          <>
            <Background>
              {flowStep === SignatureFlowStepId.DOCS && requests && <Requests store={store} />}
              {flowStep === SignatureFlowStepId.DOCS && documents && <Documents store={store} signOnly={signOnly} />}
            </Background>
              <Container maxWidth="md">
                <div className={classes.buttonWrapper}>
                  {store.flowStepIndex > 0 && (
                    <Button
                      variant="contained"
                      color="default"
                      className={`${classes.actionButton}`}
                      onClick={() => {
                        store.goBack();
                      }}
                      disabled={store.signed}
                      startIcon={<ArrowBackIos />}
                    >
                      {t('general_back')}
                    </Button>
                  )}
                  {store.canBeSigned && buttons()}
                </div>
              </Container>
          </>
        )}

        <ConfirmDialog
          isOpen={abortDialogOpen}
          title="documents_sign_abort"
          close={() => setAbortDialogOpen(false)}
          confirm={async () => {
            await store.abortSigning();
            setAbortDialogOpen(false);
          }}
          btnConfirmText="general_confirm"
        >
          <Typography>{t('documents_sign_abort_info')}</Typography>
        </ConfirmDialog>

        <Dialog open={store.canBeSigned && !completeDialogClosed} maxWidth="sm">
          <DialogHeader onClose={() => setCompleteDialogClosed(true)} />
          <DialogContent>
            <Typography style={{ textAlign: 'center' }}>{t('documents_sign_canVerify')}</Typography>
          </DialogContent>
          <DialogActions>{buttons()}</DialogActions>
        </Dialog>

        <Dialog open={store.verifyDialog} maxWidth="sm">
          <DialogHeader onClose={store.hideVeryficationDialog}>{t('documents_sign_verify_title')}</DialogHeader>
          <DialogContent>
            <Typography>
              {t('documents_sign_enterCodeInfo')} <strong>{rootStore.stores.userStore.activeAccount?.phone}</strong>
            </Typography>
            <TextField value={smsCode} onChange={(e) => setSmsCode(e.target.value)} />
            {store.wrongCode && <div className={classes.error}>{t('documents_sign_wrongCode')}</div>}
          </DialogContent>
          <DialogActions>
            <Button onClick={store.requestCode} variant="contained" color="default">
              {t('documents_sign_sendCodeAgain')}
            </Button>
            <Button onClick={() => store.verifyCode(smsCode)} variant="contained" color="primary">
              {t('general_confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          isOpen={rootStore.stores.errorStore.errorDialog}
          title="error_title"
          close={rootStore.stores.errorStore.hideErrorDialog}
          confirm={rootStore.stores.errorStore.hideErrorDialog}
        >
          <Typography>{t(rootStore.stores.errorStore.customErrorDialog || 'error_info')}</Typography>
        </ConfirmDialog>

        <ConfirmDialog
          isOpen={store.completeDialog !== null}
          title="documents_sign_complete_title"
          close={store.hideCompleteDialog}
          confirm={() => {
            store.hideCompleteDialog();
            store.logoutAfterCompletion();
          }}
        >
          <Typography>
            {t(`documents_sign_complete_info_${store.completeDialog}`)}
            {store.showLogoutAfterCompletionInfo && (
              <>
                <br />
                {t('documents_sign_complete_info_logout')}
              </>
            )}
          </Typography>
        </ConfirmDialog>
      </Container>
    </>
  ) : null;
};

export default compose(inject('rootStore'), observer)(Signature);
